import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Save, Loader2, AlertCircle, Check, ChevronDown } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { QuantitySelector } from './quantity-selector';
import { useNoshStore } from '../../lib/store';
import { getUnitValues } from '../../lib/measurement-units';
import { daysUntilExpiry } from '../../lib/utils';

// Database-accurate food categories
const FOOD_CATEGORIES = [
  'Meat', 'Fish', 'Dairy', 'Vegetables', 'Fruits', 'Grains',
  'Condiments', 'Legumes', 'Nuts', 'Sweeteners', 'Beverages',
  'Frozen', 'Other'
];

// This will be replaced with dynamic unit filtering based on user preference

// Enhanced intelligent location detection for optimal storage time
// Helper function to ensure quantity is a valid number for database
const validateQuantity = (quantity: any): number => {
  // Handle empty string during typing
  if (quantity === '' || quantity === null || quantity === undefined) {
    return 1; // Default to 1 for database operations
  }
  
  const parsed = parseFloat(String(quantity));
  // Ensure it's a valid number and positive
  if (isNaN(parsed) || parsed <= 0) {
    console.log(`🔢 [Quantity Fix] Invalid quantity "${quantity}", defaulting to 1`);
    return 1;
  }
  return parsed; // Keep precise decimal value
};

const detectItemLocation = (name: string, category: string): 'fridge' | 'pantry' | 'freezer' => {
  const itemName = name.toLowerCase();
  const itemCategory = category.toLowerCase();
  
  // Check for explicit frozen keywords first
  if (itemName.includes('frozen') || 
      itemName.includes('freeze') || 
      itemName.includes('ice cream') ||
      itemName.includes('ice') ||
      itemCategory === 'frozen') {
    return 'freezer';
  }
  
  // Pantry items for optimal storage time (items that last longer outside fridge)
  if (
    // Root vegetables and hardy produce
    itemName.includes('potato') || itemName.includes('onion') || itemName.includes('garlic') ||
    itemName.includes('ginger') || itemName.includes('shallot') || itemName.includes('sweet potato') ||
    itemName.includes('yam') || itemName.includes('turnip') || itemName.includes('rutabaga') ||
    
    // Fruits that ripen better at room temp
    itemName.includes('banana') || itemName.includes('avocado') || itemName.includes('tomato') ||
    itemName.includes('mango') || itemName.includes('papaya') || itemName.includes('kiwi') ||
    
    // Grains, legumes, nuts - shelf stable
    itemCategory.includes('grain') || itemCategory.includes('nuts') || itemCategory.includes('legume') ||
    itemCategory.includes('condiment') || itemCategory.includes('sweetener') || itemCategory.includes('beverage') ||
    
    // Pantry staples
    itemName.includes('rice') || itemName.includes('pasta') || itemName.includes('bread') ||
    itemName.includes('cereal') || itemName.includes('flour') || itemName.includes('sugar') ||
    itemName.includes('salt') || itemName.includes('honey') || itemName.includes('syrup') ||
    itemName.includes('sauce') || itemName.includes('oil') || itemName.includes('vinegar') ||
    itemName.includes('spice') || itemName.includes('herb') || itemName.includes('tea') ||
    itemName.includes('coffee') || itemName.includes('cocoa') ||
    
    // Packaged/processed foods
    itemName.includes('can') || itemName.includes('jar') || itemName.includes('bottle') ||
    itemName.includes('box') || itemName.includes('pack') || itemName.includes('bag') ||
    itemName.includes('crackers') || itemName.includes('chips') || itemName.includes('nuts') ||
    itemName.includes('dried') || itemName.includes('instant')
  ) {
    return 'pantry';
  }
  
  // Fridge items - perishables that need refrigeration for optimal storage time
  if (
    // All meat, fish, dairy
    itemCategory.includes('meat') || itemCategory.includes('fish') || itemCategory.includes('dairy') ||
    
    // Fresh produce that benefits from refrigeration
    itemCategory.includes('vegetable') || itemCategory.includes('fruit') ||
    
    // Specific perishable items
    itemName.includes('fresh') || itemName.includes('milk') || itemName.includes('cheese') ||
    itemName.includes('yogurt') || itemName.includes('butter') || itemName.includes('cream') ||
    itemName.includes('egg') || itemName.includes('lettuce') || itemName.includes('spinach') ||
    itemName.includes('broccoli') || itemName.includes('carrot') || itemName.includes('celery') ||
    itemName.includes('cucumber') || itemName.includes('pepper') || itemName.includes('apple') ||
    itemName.includes('grape') || itemName.includes('berry') || itemName.includes('orange') ||
    itemName.includes('lemon') || itemName.includes('lime') || itemName.includes('chicken') ||
    itemName.includes('beef') || itemName.includes('pork') || itemName.includes('salmon') ||
    itemName.includes('tuna') || itemName.includes('seafood') || itemName.includes('leftovers')
  ) {
    return 'fridge';
  }
  
  // Default to fridge for unknown items (safer for perishables)
  return 'fridge';
};

interface ScannedItem {
  name: string;
  category: string;
  quantity: number | string;
  unit: string;
  location: 'fridge' | 'pantry' | 'freezer';
  emoji: string;
  expiry_estimate_days: number;
}

interface ScanResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  scanData: {
    items: ScannedItem[];
    total_items_found: number;
    image_type: string;
    notes: string;
  } | null;
  scanError?: string;
}

// Helper functions for expiry styling (copied from inventory)
const getExpiryColorWithLocation = (days: number, location: string): string => {
  if (location === 'freezer') {
    return 'bg-cyan-100 text-cyan-700 dark:text-cyan-300 border border-cyan-200';
  }
  
  if (days < 0) return 'bg-destructive/20 text-destructive border border-destructive/30';
  if (days < 3) return 'bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-500/20';
  if (days < 7) return 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20';
  return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20';
};

const getExpiryDisplayText = (days: number, location: string): string => {
  if (location === 'freezer') {
    return '❄️ Frozen';
  }
  
  if (days < 0) return 'Expired';
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `${days} days`;
};

const ScanResultsModal: React.FC<ScanResultsModalProps> = ({ 
  isOpen, 
  onClose, 
  scanData, 
  scanError 
}) => {
  const { addItem, updateItem, inventory, loadAllData, user } = useNoshStore();
  
  // Get unit options based on user's measurement system preference
  const unitOptions = getUnitValues(user.measurementSystem || 'metric');
  const [editableItems, setEditableItems] = useState<ScannedItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [deletingItems, setDeletingItems] = useState<Set<number>>(new Set());

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (scanData?.items) {
      // Ensure expiry_estimate_days is a valid number and auto-detect locations
      const validatedItems = scanData.items.map(item => ({
        ...item,
        expiry_estimate_days: validateExpiryDays(item.expiry_estimate_days),
        location: detectItemLocation(item.name, item.category) // Auto-detect location
      }));
      setEditableItems(validatedItems);
    }
  }, [scanData]);

  // Validate and normalize expiry days
  const validateExpiryDays = (days: number): number => {
    // Check if days is a valid number and within reasonable bounds
    if (typeof days !== 'number' || isNaN(days) || !isFinite(days)) {
      return 7; // Default to 7 days if invalid
    }
    // Ensure the value is within reasonable bounds (e.g., -365 to 3650 days)
    return Math.max(-365, Math.min(3650, Math.round(days)));
  };

  const formatExpiryDate = (days: number): string => {
    try {
      const validDays = validateExpiryDays(days);
      const date = new Date();
      date.setHours(0, 0, 0, 0); // Reset time portion to midnight
      date.setDate(date.getDate() + validDays);
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.error('Error formatting expiry date:', error);
      // Return today's date as fallback
      return new Date().toISOString().split('T')[0];
    }
  };

  const updateEditableItem = (index: number, field: string, value: any) => {
    const updated = [...editableItems];
    if (field === 'expiry_estimate_days' && typeof value === 'number') {
      value = validateExpiryDays(value);
    }
    // Note: quantity validation is now handled only on blur, not during typing
    updated[index] = { ...updated[index], [field]: value };
    setEditableItems(updated);
  };

  const updateItemMultiple = (index: number, updates: { [key: string]: any }) => {
    const updated = [...editableItems];
    updated[index] = { ...updated[index], ...updates };
    setEditableItems(updated);
  };

  const removeItem = (index: number) => {
    if (isMobile) {
      // Add to deleting set to trigger exit animation
      setDeletingItems(prev => new Set(prev).add(index));
      
      // Remove after animation completes
      setTimeout(() => {
        const updated = editableItems.filter((_, i) => i !== index);
        setEditableItems(updated);
        setDeletingItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(index);
          return newSet;
        });
      }, 300); // Match animation duration
    } else {
      // Desktop: immediate removal
      const updated = editableItems.filter((_, i) => i !== index);
      setEditableItems(updated);
    }
  };

  const handleSave = async () => {
    if (editableItems.length === 0) return;

    // Check if user is authenticated
    if (!user?.email) {
      console.error('User not authenticated');
      return;
    }

    setIsSaving(true);
    try {
      // Process each item and handle duplicates
      for (const item of editableItems) {
        // Check if item already exists in inventory
        const existingItem = inventory.find(
          inv => inv.name.toLowerCase() === item.name.toLowerCase() && 
                 inv.location === item.location
        );

        if (existingItem) {
          // Update quantity of existing item
          await updateItem(existingItem.id, {
            quantity: existingItem.quantity + validateQuantity(item.quantity)
          });
        } else {
          // Add new item
          const newItem = {
            name: item.name,
            category: item.category,
            location: item.location,
            quantity: validateQuantity(item.quantity),
            unit: item.unit,
            expiryDate: formatExpiryDate(item.expiry_estimate_days),
            emoji: item.emoji
          };
          await addItem(newItem);
        }
      }

      // Reload data to reflect changes
      await loadAllData();
      setSaveSuccess(true);
      
      // Auto-close after success
      setTimeout(() => {
        onClose();
        setSaveSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Error saving scanned items:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center"
        style={{ backdropFilter: 'blur(8px)' }}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/60"
        />
        
        {/* Modal Content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3, ease: 'linear' }}
          className={`relative ${
            isMobile 
              ? 'w-full h-full mx-0 rounded-none flex flex-col bg-background' 
              : 'w-full max-w-4xl mx-4 max-h-[90vh] bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700'
          }`}
        >
          {/* Header */}
          <div className={`sticky top-0 ${
            isMobile 
              ? 'bg-background px-4 py-4 border-b border-gray-200 dark:border-slate-700' 
              : 'bg-white dark:bg-slate-800 p-6 border-b border-gray-200 dark:border-slate-700 rounded-t-2xl'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-heading font-semibold text-foreground">
                  Scan Results
                </h2>
                {scanData && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Found {scanData.total_items_found} items • {scanData.image_type}
                  </p>
                )}
              </div>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={onClose}
                className="rounded-full"
              >
                <X size={20} />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className={`${
            isMobile 
              ? 'flex-1 overflow-y-auto px-4 bg-background' 
              : 'p-6 overflow-y-auto max-h-96'
          }`}>
            {scanError ? (
              <div className="text-center py-12">
                <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Scan Failed</h3>
                <p className="text-muted-foreground">{scanError}</p>
              </div>
            ) : saveSuccess ? (
              <div className="text-center py-12">
                <Check size={48} className="mx-auto text-green-500 mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Items Saved!</h3>
                <p className="text-muted-foreground">Successfully added {editableItems.length} items to your inventory</p>
              </div>
            ) : editableItems.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No Items Found</h3>
                <p className="text-muted-foreground">We couldn't identify any food items in this image</p>
              </div>
            ) : (
              <div className={`${isMobile ? '' : ''}`}>
                {scanData?.notes && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      {scanData.notes}
                      {scanData.notes && !scanData.notes.toLowerCase().includes('expiry') && (
                        <span className="block mt-2">
                          📅 Expiry dates are estimated based on typical shelf life and can be adjusted for each item.
                        </span>
                      )}
                    </p>
                  </div>
                )}
                
                {/* Compact Table Layout */}
                <div className={`space-y-2 ${isMobile ? 'pb-24' : ''}`}>
                  {/* Header Row - Desktop only */}
                  {!isMobile && (
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wide border-b border-gray-200 dark:border-slate-700">
                      <div className="col-span-3">Item</div>
                      <div className="col-span-1">Qty</div>
                      <div className="col-span-1">Unit</div>
                      <div className="col-span-2">Category</div>
                      <div className="col-span-2">Location</div>
                      <div className="col-span-2">Expiry Date</div>
                      <div className="col-span-1"></div>
                    </div>
                  )}
                  
                  {/* Item Rows */}
                  {editableItems.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ 
                        opacity: deletingItems.has(index) ? 0 : 1, 
                        y: 0,
                        scale: deletingItems.has(index) ? 0.95 : 1,
                        x: deletingItems.has(index) ? (isMobile ? -20 : 0) : 0
                      }}
                      exit={{ 
                        opacity: 0, 
                        scale: 0.95, 
                        x: isMobile ? -20 : 0,
                        transition: { duration: 0.3, ease: 'easeInOut' }
                      }}
                      transition={{ 
                        duration: deletingItems.has(index) ? 0.3 : 0.02, 
                        delay: deletingItems.has(index) ? 0 : index * 0.02,
                        layout: { duration: 0.3, ease: 'easeInOut' }
                      }}
                      layout
                      className={`${
                        isMobile 
                          ? 'bg-white dark:bg-slate-800 border-0 rounded-xl p-2.5 space-y-1.5 shadow-sm' 
                          : 'grid grid-cols-12 gap-2 items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-700/30 rounded-lg border border-transparent hover:border-gray-200 dark:hover:border-slate-600 transition-colors'
                      }`}
                    >
                      {isMobile ? (
                        // Mobile: Compact iOS-style layout
                        <>
                          {/* Row 1: Item Name + Remove Button */}
                          <div className="flex items-center gap-2 pb-1.5">
                            <span className="text-lg">{item.emoji}</span>
                            <Input
                              value={item.name}
                              onChange={(e) => updateEditableItem(index, 'name', e.target.value)}
                              className="text-sm h-6 flex-1 font-medium border-0 bg-transparent p-0 focus:ring-0 focus-visible:ring-0"
                              placeholder="Item name"
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeItem(index)}
                              className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full"
                            >
                              <X size={14} />
                            </Button>
                          </div>
                          
                          {/* Row 2: Quantity + Unit + Category (3 columns) */}
                          <div className="flex items-center gap-1.5 pb-1.5">
                            <div className="w-16">
                              {/* Mobile: Quantity input field for flexible quantities */}
                              <input
                                type="number"
                                value={item.quantity || ''}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value);
                                  if (!isNaN(value) && value > 0) {
                                    updateEditableItem(index, 'quantity', value);
                                  } else if (e.target.value === '') {
                                    // Allow empty value during typing
                                    updateEditableItem(index, 'quantity', '');
                                  }
                                }}
                                onBlur={(e) => {
                                  // Ensure valid value when input loses focus
                                  const numQuantity = typeof item.quantity === 'string' ? parseFloat(item.quantity) : item.quantity;
                                  if (!item.quantity || isNaN(numQuantity) || numQuantity <= 0) {
                                    updateEditableItem(index, 'quantity', 1);
                                  }
                                }}
                                min="0.001"
                                max="9999"
                                step="any"
                                className="w-full h-6 text-xs bg-gray-50 dark:bg-slate-700 border-0 rounded-lg px-2 text-foreground focus:outline-none focus:ring-0 text-center font-medium"
                                placeholder="1"
                              />
                            </div>
                            <div className="flex-1">
                              <input
                                type="text"
                                value={item.unit}
                                onChange={(e) => updateEditableItem(index, 'unit', e.target.value)}
                                className="w-full h-6 text-xs bg-gray-50 dark:bg-slate-700 border-0 rounded-lg px-2 text-foreground focus:outline-none focus:ring-0 text-center font-medium"
                                placeholder="unit"
                              />
                            </div>
                            <div className="flex-1 relative">
                              <select
                                value={item.category}
                                onChange={(e) => {
                                  const newCategory = e.target.value;
                                  const newLocation = detectItemLocation(item.name, newCategory);
                                  updateItemMultiple(index, {
                                    category: newCategory,
                                    location: newLocation
                                  });
                                }}
                                className="w-full h-6 text-xs bg-gray-50 dark:bg-slate-700 border-0 rounded-lg px-2 pr-5 text-foreground appearance-none focus:outline-none focus:ring-0 text-center font-medium"
                              >
                                {FOOD_CATEGORIES.map(category => (
                                  <option key={category} value={category}>{category}</option>
                                ))}
                              </select>
                              <ChevronDown size={8} className="absolute right-1.5 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none" />
                            </div>
                          </div>
                          
                          {/* Row 3: Location (full width) */}
                          <div className="flex items-center gap-1.5 pb-1.5">
                            <span className="text-xs text-muted-foreground font-medium w-16">Location:</span>
                            <div className="flex-1 grid grid-cols-3 gap-1">
                              {(['fridge', 'pantry', 'freezer'] as const).map((location) => (
                                <button
                                  key={location}
                                  onClick={() => updateItemMultiple(index, { location: location as 'fridge' | 'pantry' | 'freezer' })}
                                  className={`h-6 rounded-lg text-xs font-medium transition-colors ${
                                    item.location === location 
                                      ? 'bg-primary text-white' 
                                      : 'bg-gray-50 dark:bg-slate-700 text-muted-foreground hover:bg-gray-100 dark:hover:bg-slate-600'
                                  }`}
                                >
                                  {location === 'fridge' && '🧊'}
                                  {location === 'pantry' && '🏠'}
                                  {location === 'freezer' && '❄️'}
                                  <span className="ml-1 capitalize">{location}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          {/* Row 4: Expiry Date */}
                          <div className="flex items-center gap-1.5">
                            <div className="flex-1">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-muted-foreground font-medium">Expires:</span>
                                <div 
                                  className="relative cursor-pointer flex-1"
                                  onClick={() => {
                                    const dateInput = document.getElementById(`date-input-${index}`) as HTMLInputElement;
                                    if (dateInput) {
                                      if (dateInput.showPicker) {
                                        dateInput.showPicker();
                                      } else {
                                        dateInput.focus();
                                      }
                                    }
                                  }}
                                >
                                  <div className="flex items-center h-8 bg-gray-50 dark:bg-slate-700 border-0 rounded-lg px-3 text-sm">
                                    <Calendar size={12} className="text-muted-foreground mr-2" />
                                    <span className="flex-1 text-foreground font-medium">
                                      {new Date(formatExpiryDate(item.expiry_estimate_days)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </span>
                                  </div>
                                  <input
                                    id={`date-input-${index}`}
                                    type="date"
                                    value={formatExpiryDate(item.expiry_estimate_days)}
                                    onChange={(e) => {
                                      const selectedDate = new Date(e.target.value);
                                      const today = new Date();
                                      today.setHours(0, 0, 0, 0);
                                      const diffTime = selectedDate.getTime() - today.getTime();
                                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                      updateEditableItem(index, 'expiry_estimate_days', diffDays);
                                    }}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              <span className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${getExpiryColorWithLocation(item.expiry_estimate_days, item.location)}`}>
                                {getExpiryDisplayText(item.expiry_estimate_days, item.location)}
                              </span>
                            </div>
                          </div>
                        </>
                      ) : (
                        // Desktop: Single row layout
                        <>
                          {/* Item Name with Emoji - 3 cols */}
                          <div className="col-span-3 flex items-center gap-2">
                            <span className="text-base">{item.emoji}</span>
                            <Input
                              value={item.name}
                              onChange={(e) => {
                                const newName = e.target.value;
                                const newLocation = detectItemLocation(newName, item.category);
                                updateItemMultiple(index, {
                                  name: newName,
                                  location: newLocation
                                });
                              }}
                              className="text-sm h-7 flex-1"
                              placeholder="Item name"
                            />
                          </div>
                          
                          {/* Quantity Input - 1 col */}
                          <div className="col-span-1">
                            <input
                              type="number"
                              value={item.quantity || ''}
                              onChange={(e) => {
                                const value = parseFloat(e.target.value);
                                if (!isNaN(value) && value > 0) {
                                  updateEditableItem(index, 'quantity', value);
                                } else if (e.target.value === '') {
                                  // Allow empty value during typing
                                  updateEditableItem(index, 'quantity', '');
                                }
                              }}
                              onBlur={(e) => {
                                // Ensure valid value when input loses focus
                                const numQuantity = typeof item.quantity === 'string' ? parseFloat(item.quantity) : item.quantity;
                                if (!item.quantity || isNaN(numQuantity) || numQuantity <= 0) {
                                  updateEditableItem(index, 'quantity', 1);
                                }
                              }}
                              min="0.001"
                              max="9999"
                              step="any"
                              className="w-full h-7 text-sm bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-md px-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-center"
                              placeholder="1"
                            />
                          </div>
                          
                          {/* Unit Input - 1 col */}
                          <div className="col-span-1">
                            <input
                              type="text"
                              value={item.unit}
                              onChange={(e) => updateEditableItem(index, 'unit', e.target.value)}
                              className="w-full h-7 text-sm bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-md px-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-center"
                              placeholder="unit"
                            />
                          </div>
                          
                          {/* Category Dropdown - 2 cols */}
                          <div className="col-span-2 relative">
                            <select
                              value={item.category}
                              onChange={(e) => {
                                const newCategory = e.target.value;
                                const newLocation = detectItemLocation(item.name, newCategory);
                                updateItemMultiple(index, {
                                  category: newCategory,
                                  location: newLocation
                                });
                              }}
                              className="w-full h-7 text-sm bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-md px-2 pr-6 text-foreground appearance-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-center font-medium"
                            >
                              {FOOD_CATEGORIES.map(category => (
                                <option key={category} value={category}>{category}</option>
                              ))}
                            </select>
                            <ChevronDown size={12} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none" />
                          </div>
                          
                          {/* Location Dropdown - 2 cols */}
                          <div className="col-span-2 relative">
                            <select
                              value={item.location}
                              onChange={(e) => updateItemMultiple(index, { location: e.target.value as 'fridge' | 'pantry' | 'freezer' })}
                              className="w-full h-7 text-sm bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-md px-2 pr-6 text-foreground appearance-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                              <option value="fridge">🧊 Fridge</option>
                              <option value="pantry">🏠 Pantry</option>
                              <option value="freezer">❄️ Freezer</option>
                            </select>
                            <ChevronDown size={12} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none" />
                          </div>
                          
                          {/* Expiry Date - 2 cols (increased from 1) */}
                          <div className="col-span-2">
                            <div 
                              className="relative cursor-pointer"
                              onClick={() => {
                                const dateInput = document.getElementById(`date-input-${index}`) as HTMLInputElement;
                                if (dateInput) {
                                  if (dateInput.showPicker) {
                                    dateInput.showPicker();
                                  } else {
                                    dateInput.focus();
                                  }
                                }
                              }}
                            >
                              <div className="flex items-center h-7 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-md px-2 text-sm">
                                <Calendar size={12} className="text-muted-foreground mr-1.5" />
                                <span className="flex-1 text-foreground text-sm">
                                  {new Date(formatExpiryDate(item.expiry_estimate_days)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
                                </span>
                              </div>
                              <input
                                id={`date-input-${index}`}
                                type="date"
                                value={formatExpiryDate(item.expiry_estimate_days)}
                                onChange={(e) => {
                                  const selectedDate = new Date(e.target.value);
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  const diffTime = selectedDate.getTime() - today.getTime();
                                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                  updateEditableItem(index, 'expiry_estimate_days', diffDays);
                                }}
                                className="absolute inset-0 opacity-0 cursor-pointer"
                              />
                            </div>
                          </div>
                          
                          {/* Remove Button - 1 col */}
                          <div className="col-span-1 flex justify-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeItem(index)}
                              className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                              <X size={12} />
                            </Button>
                          </div>
                        </>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer - Sticky iOS-style buttons */}
          {!scanError && !saveSuccess && editableItems.length > 0 && (
            <div className={`${
              isMobile 
                ? 'fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 p-4 z-50' 
                : 'sticky bottom-0 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 p-6 rounded-b-2xl'
            }`}>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className={`flex-1 ${
                    isMobile 
                      ? 'h-12 rounded-2xl text-base font-medium' 
                      : ''
                  }`}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  className={`flex-1 bg-primary hover:bg-primary/90 ${
                    isMobile 
                      ? 'h-12 rounded-2xl text-base font-medium shadow-lg' 
                      : ''
                  }`}
                  disabled={isSaving || editableItems.length === 0}
                >
                  {isSaving ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} className="mr-2" />
                      Save {editableItems.length} Items
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default ScanResultsModal;